"use client";

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { StoryblokRichText } from "@storyblok/react";
import { ProjectProps } from "@/types/component.types";
import classNames from "classnames";
import Headline from "@/components/ui/Headline";
import Button from "@/components/ui/Button";
import Image from "next/image";
import Text from "@/components/ui/Text";
import Checkbox from "@/components/ui/Checkbox";
import UserGroupIcon from "@/components/icons/UserGroup.icon";
import UserDuoIcon from "@/components/icons/UserDuo.icon";
import UserSoloIcon from "@/components/icons/UserSolo.icon";
import ArrowRightIcon from "@/components/icons/ArrowRight.icon";

const translations = {
  en: {
    title: "You are leaving the site.",
    description: "You are about to visit a third-party website. I am not responsible for their content or privacy practices. Do you wish to continue?",
    checkbox: "Don't show this warning again",
    cancel: "Cancel",
    continue: "Continue",
  },
  de: {
    title: "Sie verlassen die Website.",
    description: "Sie werden auf eine externe Website weitergeleitet. Ich bin nicht für deren Inhalte oder Datenschutzpraktiken verantwortlich. Möchten Sie fortfahren?",
    checkbox: "Diese Warnung nicht mehr anzeigen",
    cancel: "Abbrechen",
    continue: "Weiter",
  }
};

const TeamSizeIcon = ({
  className = "",
  size,
}: {
  className?: string;
  size: "1" | "2" | "3";
}) => {
  switch (size) {
    case "1":
      return <UserSoloIcon size={20} className={className} />;
    case "2":
      return <UserDuoIcon size={20} className={className} />;
    case "3":
      return  <UserGroupIcon size={20} className={className} />;
    default:
      return null;
  }
};

export default function Project({ 
  blok, 
  language = "en" 
}: { 
  blok: ProjectProps;
  language?: "en" | "de"; 
}) {

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [mounted, setMounted] = useState(false);

  const t = translations[language];
  const targetUrl = blok.link?.[0]?.link?.cached_url || blok.link?.[0]?.link?.url;

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLinkClick = (e: React.MouseEvent) => {
    const skipWarning = localStorage.getItem("skipExternalWarning") === "true";
    if (skipWarning) return; 

    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleContinue = () => {
    if (dontShowAgain) {
      localStorage.setItem("skipExternalWarning", "true");
    }
    setIsModalOpen(false);
    if (targetUrl) {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setDontShowAgain(false);
  };

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isModalOpen]);

  return (
    <div className={classNames(
      "mt-8 md:mt-10 xl:-mt-4 first-of-type:mt-10 max-w-[1020px] duration-300",
      "pointer-events-none flex relative",
    
      // (1st): (text L, img R)
      "md:even:ml-auto md:even:flex-row",
      "md:even:[&_.project-card]:items-right",
      
      // (2nd): (img L, text R)
      "odd:mr-auto md:odd:flex-row-reverse",
      "odd:[&_.project-card]:items-left" 
    )}>
      
      <div 
        className={classNames(
          "project-card pointer-events-auto relative w-full",
          "p-6 md:p-8 md:py-14",
          "grid grid-cols-1 md:grid-cols-2 items-center gap-6 md:gap-12",
          "bg-beige-dark dark:bg-neutral-800",
          "border border-beige-dark dark:border-neutral-800",
          "rounded-2xl shadow-[3px_3px_8px_5px_rgba(0,0,0,0.15)]"
        )}
      >
        
        {/* text */}
        <div className="flex-1 space-y-3 xl:space-y-4">
          <div className="flex items-center gap-2 mb-1">
            {blok.title && (
              <span className="block text-sm font-semibold tracking-wider text-green uppercase">
                {blok.title}
              </span>
            )}
            {blok.team_size && (
              <TeamSizeIcon size={blok.team_size} className="text-green" />
            )}
          </div>

          <Headline
            text={blok.headline?.[0]?.text}
            level={blok.headline?.[0]?.level}
          />

          {blok.description && (
            <StoryblokRichText 
              doc={blok.description} 
            />
          )}
        
          {targetUrl && (
            <Button
              variant="tertiary"
              href={targetUrl}
              onClick={handleLinkClick}
              target="_blank"
              rel="noopener noreferrer"
              icon={<ArrowRightIcon />}
            >
              {blok.link[0].label}
            </Button>
          )}
        </div>

        {/* image */}
        {blok.img?.[0]?.src?.filename && (
          <div className="flex-1 w-full flex items-center justify-center">
            <Image 
              className="rounded-xl object-cover w-full shadow-sm aspect-1905/945"
              src={blok.img[0].src.filename} 
              alt={blok.img[0].alt || blok.title} 
              width={1905}
              height={945}
              loading="lazy"
            />
          </div>
        )}
      </div>

      {/* warning modal */}
      {mounted && isModalOpen && createPortal(
        <div 
          className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm pointer-events-auto cursor-default"
          onClick={handleCancel}
        >
          <div 
            className="bg-white dark:bg-neutral-900 p-6 rounded-xl shadow-xl max-w-[500px] w-full border-3 border-neutral-200 dark:border-neutral-700 animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <Headline
              className="mb-2"
              text={t.title}
              level="h3"
              tag="p"
            />
            <Text
              className="mb-6"
              color="text-neutral-600 dark:text-neutral-400"
              variant="small"
            >
              {t.description}
            </Text>
            
            <Checkbox
              className="mb-8"
              boxClassName="hover:cursor-pointer!"
              label={t.checkbox}
              onChange={setDontShowAgain}
              checked={dontShowAgain}
            />

            <div className="flex gap-3">
              <Button 
                className="hover:cursor-pointer!"
                variant="primary-warning"
                onClick={handleCancel}
              >
                {t.cancel}
              </Button>
              <Button 
                className="hover:cursor-pointer!"
                variant="primary"
                onClick={handleContinue}
              >
                {t.continue}
              </Button>
            </div>
          </div>
        </div>,
        document.body // append modal directly to body tag
      )}
    </div>
  );
}